import { stringifyWithLocalCalendarDates } from './date';

const ISO_DATE_REGEX =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.{0,1}\d*))(?:Z|(\+|-)([\d|:]*))?$/;

/**
 * Parsed the incoming object which if it has Pascal Cased properties they will be changed to lower camel cased e.g. { Name: 'Adam' } will be changed to { name: 'Adam'}
 * As lower camel casing is used in Angular/Client side typescript but API use Pascal cased responses (until they are updated to have JSON serializer to serialize as lower camel cased)
 * @param res Example of usage: Observable<MyFooClass[]>this.http.get(url).map(response => parseDataToLowerCamelCased(response));
 */
export function parseDataToLowerCamelCased<T = unknown>(
  res:
    | {
        body?: string;
        _body?: string;
      }
    | T
): T {
  const source =
    typeof res === 'object' && res !== null && ('body' in res || '_body' in res)
      ? res.body || res._body || stringifyWithLocalCalendarDates(res)
      : stringifyWithLocalCalendarDates(res);

  const body = JSON.parse(source, function (_key, value) {
    const revivedDate = tryReviveIsoDate(value);
    if (revivedDate !== value) {
      return revivedDate;
    } else if (value && typeof value === 'object') {
      for (const k in value) {
        if (/^[A-Z]/.test(k) && Object.hasOwnProperty.call(value, k)) {
          value[k.charAt(0).toLowerCase() + k.substring(1)] = value[k];
          delete value[k];
        }
      }
    }

    return value;
  });
  return body as T;
}

export function parseData<T = unknown>(
  res: { body?: string; _body?: string } | T | null
): T | null {
  let body: T | null = null;
  try {
    body = res
      ? JSON.parse(
          (res as { body?: string; _body?: string }).body ||
            (res as { _body?: string })._body ||
            stringifyWithLocalCalendarDates(res),
          parseDate
        )
      : null;
  } catch {}
  return body;
}

/**
 * When calling this function from a promise response, it doesn't have access to this.parseDate... but if
 * we duplicate data from anywhere and need to use this function, its copied from above.
 *
 * self.filteredData = deepClone(self.data);
 *
 * @param key
 * @param value
 */
export function parseDate(_key: unknown, value: unknown) {
  return tryReviveIsoDate(value);
}

export function tryReviveIsoDate<T>(value: T): T | Date {
  if (typeof value !== 'string') {
    return value;
  }

  if (!ISO_DATE_REGEX.test(value)) {
    return value;
  }

  const parsedValue = new Date(value);
  return Number.isNaN(parsedValue.getTime()) ? value : parsedValue;
}

export function reviveDatesDeep<T>(value: T): T {
  if (value == null) {
    return value;
  }

  const revivedValue = tryReviveIsoDate(value);
  if (revivedValue !== value) {
    return revivedValue as T;
  }

  if (value instanceof Date) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => reviveDatesDeep(item)) as T;
  }

  if (typeof value !== 'object') {
    return value;
  }

  const output = Object.create(
    Object.getPrototypeOf(value) ?? Object.prototype
  );
  for (const [key, nestedValue] of Object.entries(value)) {
    output[key] = reviveDatesDeep(nestedValue);
  }

  return output;
}
